import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import * as XML2JS from 'xml2js';
import { DeviceService } from '../device/device.service';
import { DiskService } from '../disk/disk.service';
import { DB_KEY_PREFIXES, DB_SYNC_STATE_KEYS } from '../../config/pouch-db.config';
import { MarketScanDataService } from '../../data-services/market-scan/market-scan.data.service';
import { EventsService } from '../events/events.service';
import { DynamicFormsService } from '../dynamic-forms/dynamic-forms-service';
import { FormType } from '../../classes/dynamic-form/dynamic-form.class';

/** Disk key the generic configured-forms linked-entity fetch (ContactOfflineService.
 * fetchContactsLinkedEntityData) persists Customer_Segmentation subgrid rows under — keyed by
 * the subgrid's own referencingEntity name, not one of our own DB_KEY_PREFIXES. */
const CONFIGURED_FORMS_PRODUCTRATING_DISK_KEY = 'indskr_productrating';

/**
 * Published (no payload) whenever getRawRows() starts reflecting a new fetch/disk-load.
 * Unlike the adoption-ladder sibling (ProductRatingOfflineService), this sync is awaited as
 * part of core sync (MasterDataService's contacts-adjacent sync stage) rather than fire-and-
 * forget, so consumers should see fresh data very shortly after sync starts — this event still
 * exists for the loadFromDB-only/offline path and for any consumer that renders before that
 * stage resolves.
 */
export const CUSTOMER_SEGMENTATION_DATA_UPDATED_EVENT = 'customerSegmentationData:updated';

/**
 * Raw (ungrouped) indskr_productrating row carrying segmentation only (segmentation and
 * adoption ladder live on mutually exclusive rows of the same entity — see
 * MarketScanDataService.buildProductRatingFetchXml). Position-scoped to the current user's
 * territory. Single fetch/cache shared by MarketScanService (groups by segment),
 * ContactOfflineService (groups by contact) and any AI tool needing per-contact segmentation.
 */
export interface CustomerSegmentationRawRow {
  indskr_productratingid: string;
  rowStatecode: number;
  indskr_customersegmentationid: string | null;
  indskr_name: string | null;
  productId: string;
  contactId: string;
  adoptionLadderId: string;
}

@Injectable({
  providedIn: 'root'
})
export class CustomerSegmentationOfflineService {
  private rawRows: CustomerSegmentationRawRow[] = [];

  /** Segmentation is awaited during core sync, so this reaches 'ready' quickly — kept for the
   * loadFromDB-only/offline path and for any consumer that renders before that stage resolves. */
  public readonly syncState$ = new BehaviorSubject<'idle' | 'syncing' | 'ready' | 'error'>('idle');

  private usesConfiguredFormsRouteCache: boolean | null = null;

  constructor(
    private readonly deviceService: DeviceService,
    private readonly diskService: DiskService,
    private readonly marketScanDataService: MarketScanDataService,
    private readonly events: EventsService,
    private readonly dynamicFormsService: DynamicFormsService,
  ) {}

  /** Last-known merged raw-row set, synchronously. Empty until the first sync/loadFromDB call. */
  public getRawRows(): CustomerSegmentationRawRow[] {
    return this.rawRows;
  }

  /**
   * True when this org's Customer_Segmentation subgrid (Contact DISPLAYFORM) has Dynamics-side
   * custom scoping beyond the baseline single join to indskr_customersegmentation — e.g. an
   * omp_salesline-style position/sales-line eligibility chain. Confirmed with product/Dynamics:
   * such orgs must source segmentation from that configured-forms fetch instead of our own
   * position/parentposition fetch below, since only the org's own form config can express that
   * custom scoping. That fetch already runs in bulk during core sync (see ContactOfflineService.
   * fetchContactsForConfiguredDisplay, wired into ContactOfflineService.syncContacts) — no
   * per-contact live fetch involved either way. Cached for the process lifetime since the form
   * config doesn't change mid-session; ContactOfflineService calls this same method (not its own
   * copy) so both sides of the routing decision can never disagree.
   */
  public async usesConfiguredFormsRoute(): Promise<boolean> {
    if (this.usesConfiguredFormsRouteCache !== null) {
      return this.usesConfiguredFormsRouteCache;
    }
    const subgridQuery = this.getCustomerSegmentationSubgridQuery();
    this.usesConfiguredFormsRouteCache = subgridQuery ? this.hasCustomFieldsInSubgridQuery(subgridQuery) : false;
    console.log(`[customer_segmentation_sync] usesConfiguredFormsRoute: ${this.usesConfiguredFormsRouteCache} `
      + `(subgrid ${subgridQuery ? 'found' : 'not found'} on Contact DISPLAYFORM)`);
    return this.usesConfiguredFormsRouteCache;
  }

  /** Finds the Customer_Segmentation subgrid control (Subgrid.ReferencingEntity ===
   * 'indskr_productrating') on the Contact DISPLAYFORM and returns its raw SubgridQuery XML, or
   * undefined if the org's form doesn't define that subgrid at all (falls back to the default
   * form only has the baseline subgrids — treated as "no custom fields"). */
  private getCustomerSegmentationSubgridQuery(): string | undefined {
    const contactForm = this.dynamicFormsService.getFormDefinitionForEntity('contact', FormType.DISPLAYFORM);
    if (!contactForm?.metadata) {
      return undefined;
    }
    for (const tab of contactForm.metadata) {
      const control = tab.controls?.find(c => !c.dataType && c.subgrid?.referencingEntity === CONFIGURED_FORMS_PRODUCTRATING_DISK_KEY);
      if (control) {
        return control.subgrid.subgridQuery;
      }
    }
    return undefined;
  }

  /** True when any link-entity inside the subgrid's query has a nested link-entity child — the
   * baseline/default Customer_Segmentation subgrid is a flat single join to
   * indskr_customersegmentation with no further nesting; a client-added eligibility chain (e.g.
   * indskr_customersegmentation -> indskr_positiontype -> position -> systemuser) nests beneath
   * it. Exported as public (rather than folded into usesConfiguredFormsRoute) so it's unit-testable
   * against a raw XML string independent of the live form fetch. */
  public hasCustomFieldsInSubgridQuery(subgridQueryXml: string): boolean {
    if (!subgridQueryXml) {
      return false;
    }
    let hasNestedLinkEntity = false;
    const walk = (linkEntities: any[]): void => {
      if (!Array.isArray(linkEntities)) {
        return;
      }
      for (const linkEntity of linkEntities) {
        if (linkEntity?.['link-entity']) {
          hasNestedLinkEntity = true;
          return;
        }
      }
    };
    try {
      XML2JS.parseString(subgridQueryXml, (err, data) => {
        if (err) {
          console.error('[customer_segmentation_sync] hasCustomFieldsInSubgridQuery: failed to parse SubgridQuery', err);
          return;
        }
        walk(data?.fetch?.entity?.[0]?.['link-entity']);
      });
    } catch (error) {
      console.error('[customer_segmentation_sync] hasCustomFieldsInSubgridQuery: unexpected error', error);
    }
    return hasNestedLinkEntity;
  }

  public async loadFromDB(): Promise<CustomerSegmentationRawRow[]> {
    if (await this.usesConfiguredFormsRoute()) {
      this.rawRows = await this.readConfiguredFormsCacheFromDB();
    } else {
      await this.diskService.retrieve(DB_KEY_PREFIXES.CUSTOMER_SEGMENTATION_RAW_ROWS).then((data) => {
        if (data && data['raw']) {
          this.rawRows = data['raw'];
        }
      }).catch((err) => console.error('[customer_segmentation_sync] loadFromDB: Error loading raw rows from offline db!', err));
    }
    console.log(`[customer_segmentation_sync] offline-restore: ${this.rawRows.length} rows loaded from disk`);
    console.log(`[customer_segmentation_sync] publishing ${CUSTOMER_SEGMENTATION_DATA_UPDATED_EVENT} (source=loadFromDB, rows=${this.rawRows.length})`);
    this.syncState$.next('ready');
    this.events.publish(CUSTOMER_SEGMENTATION_DATA_UPDATED_EVENT);
    return this.rawRows;
  }

  /** Adapts the configured-forms fetch's already-merged, already-current disk cache (see
   * ContactOfflineService.fetchContactsLinkedEntityData, which owns its own delta-merge) into our
   * raw-row shape. productId is always null here — this org's subgrid config only selects
   * indskr_segmentation, not indskr_product; acceptable since segmentation is customer-level, not
   * product-level, and every existing consumer already tolerates a null/'other' productId. The
   * not-null filter on indskr_segmentation is defensive here (redundant with the condition already
   * injected into that fetch — see fetchContactsLinkedEntityData) in case older cached data
   * predates that fix. */
  private async readConfiguredFormsCacheFromDB(): Promise<CustomerSegmentationRawRow[]> {
    const data = await this.diskService.retrieve(CONFIGURED_FORMS_PRODUCTRATING_DISK_KEY)
      .catch((err) => { console.error('[customer_segmentation_sync] readConfiguredFormsCacheFromDB: Error loading from offline db!', err); return undefined; });
    const raw: any[] = Array.isArray(data?.['raw']) ? data['raw'] : [];
    return raw
      .filter(row => row['indskr_productrating.indskr_segmentation'])
      .map(row => ({
        indskr_productratingid: row['indskr_productrating.indskr_productratingid'],
        rowStatecode: row['indskr_productrating.statecode'],
        indskr_customersegmentationid: row['indskr_productrating.indskr_segmentation'],
        indskr_name: row['indskr_productrating.indskr_segmentation@OData.Community.Display.V1.FormattedValue']
          ?? row['indskr_productrating.indskr_segmentation_Formatted'] ?? null,
        productId: null,
        contactId: row['contactid'],
        adoptionLadderId: null,
      }));
  }

  /** Master (id -> name) list of active indskr_customersegmentation records, used to null out any
   * segmentationId on a productrating row that points to a since-deactivated record. */
  private async syncSegmentationMasterData(): Promise<Map<string, string> | undefined> {
    try {
      const masterData = await this.marketScanDataService.getCustomerSegmentationMasterData();
      await this.diskService.updateOrInsert(DB_KEY_PREFIXES.CUSTOMER_SEGMENTATION_MASTER_DATA, () => ({ raw: masterData }))
        .catch((err) => console.error('CustomerSegmentationOfflineService.syncSegmentationMasterData: Error saving master data to offline db!', err));
      return new Map(masterData.map(entry => [entry.id, entry.name]));
    } catch (error) {
      // Fail OPEN (undefined -> getSegmentationRatingsRaw treats it as pass-through) rather than
      // fail closed with an empty Map, which would null out every segmentationId in the whole
      // dataset just because this one small fetch failed.
      console.log('Error retrieving customer segmentation master data ', error);
      return undefined;
    }
  }

  /** Fetches + delta-merges indskr_productrating (segmentation-only) raw rows, persists them,
   * and returns the full merged set. Meant to be awaited directly from core sync — see
   * MarketScanService.syncCustomerSegmentationsOnly, which awaits ContactOfflineService.
   * syncContacts first specifically so this branch has fresh data to read once it runs. */
  public async sync(forceFullSync = false): Promise<CustomerSegmentationRawRow[]> {
    if (this.deviceService.isOffline) {
      console.log('[customer_segmentation_sync] device offline — falling back to loadFromDB() instead of a live fetch');
      return this.loadFromDB();
    }

    if (await this.usesConfiguredFormsRoute()) {
      this.syncState$.next('syncing');
      try {
        // No fetch of our own here — ContactOfflineService.syncContacts (via
        // fetchContactsForConfiguredDisplay) already fetched and persisted this org's
        // Customer_Segmentation subgrid data as part of the same sync stage; this only reads it.
        this.rawRows = await this.readConfiguredFormsCacheFromDB();
        console.log(`[customer_segmentation_sync] configured-forms route: ${this.rawRows.length} rows read from disk`);
        this.syncState$.next('ready');
        this.events.publish(CUSTOMER_SEGMENTATION_DATA_UPDATED_EVENT);
        return this.rawRows;
      } catch (error) {
        console.error('[customer_segmentation_sync] configured-forms route: sync failed', error);
        this.syncState$.next('error');
        throw error;
      }
    }

    this.syncState$.next('syncing');
    try {
      const activeSegmentMap = await this.syncSegmentationMasterData();
      console.log(`[customer_segmentation_sync] segmentation master data map: ${activeSegmentMap ? activeSegmentMap.size + ' active segments' : 'undefined (fetch failed — pass-through mode)'}`);
      // Confirmed via live testing: getSyncState() returns raw null when no prior state doc exists —
      // it does NOT auto-create one. Must construct and upsert a fresh doc ourselves the first time.
      const syncState = (await this.diskService.getSyncState(DB_SYNC_STATE_KEYS.SYNC_CUSTOMER_SEGMENTATION))
        ?? { _id: DB_SYNC_STATE_KEYS.SYNC_CUSTOMER_SEGMENTATION };
      const isInitialSync = forceFullSync || !syncState.lastUpdatedTime;
      // Captured before the fetch starts (not derived from server data) so we don't miss anything
      // modified during the fetch's own round-trip.
      const newLastUpdatedTime = new Date().getTime().toString();

      const rows = await this.marketScanDataService.getSegmentationRatingsRaw(
        activeSegmentMap, isInitialSync ? undefined : syncState.lastUpdatedTime);

      if (isInitialSync) {
        this.rawRows = rows;
      } else {
        this.mergeRawRows(rows);
      }

      // upsert=true (needs _id, drops any _rev) when this doc doesn't exist yet on disk;
      // upsert=false (needs an existing _rev) when we're updating a doc getSyncState found.
      const isNewSyncStateDoc = !syncState._rev;
      syncState.lastUpdatedTime = newLastUpdatedTime;
      await this.diskService.updateSyncState(syncState, isNewSyncStateDoc);
      await this.diskService.updateOrInsert(DB_KEY_PREFIXES.CUSTOMER_SEGMENTATION_RAW_ROWS, () => ({ raw: this.rawRows }))
        .catch((err) => console.error('CustomerSegmentationOfflineService.sync: Error saving raw rows to offline db!', err));

      console.log(`[customer_segmentation_sync] ${isInitialSync ? 'full' : 'delta'} sync: ${rows.length} rows fetched, ${this.rawRows.length} total rows cached`);
      console.log(`[customer_segmentation_sync] publishing ${CUSTOMER_SEGMENTATION_DATA_UPDATED_EVENT} (source=sync, rows=${this.rawRows.length})`);
      this.syncState$.next('ready');
      this.events.publish(CUSTOMER_SEGMENTATION_DATA_UPDATED_EVENT);
      return this.rawRows;
    } catch (error) {
      console.error('[customer_segmentation_sync] sync failed', error);
      this.syncState$.next('error');
      throw error;
    }
  }

  /** Upserts (active) or removes (inactive) delta rows into the maintained raw-row cache, keyed by
   * indskr_productratingid. Mutates rawRows in place. */
  private mergeRawRows(deltaRows: CustomerSegmentationRawRow[]): void {
    const rowsById = new Map(this.rawRows.map(row => [row.indskr_productratingid, row]));
    for (const row of deltaRows) {
      if (row.rowStatecode === 0) {
        rowsById.set(row.indskr_productratingid, row);
      } else {
        rowsById.delete(row.indskr_productratingid);
      }
    }
    this.rawRows = Array.from(rowsById.values());
  }
}
